import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api, apiRequest } from '../../utils/api';

type LegalSlug = 'privacy' | 'terms' | 'cookie' | 'data-deletion' | 'aup' | 'dmca';
type Status = 'draft' | 'published' | 'archived';

interface LegalDoc {
  id: string;
  slug: LegalSlug;
  title: string;
  version: string;
  body: string | null;
  status: Status;
  effectiveDate: string | null;
}

interface FormState {
  slug: LegalSlug;
  title: string;
  version: string;
  body: string;
  effective_date: string;
}

// The fixed set of legal documents + their default titles.
const SLUG_META: Record<LegalSlug, string> = {
  privacy: 'Privacy Policy',
  terms: 'Terms of Use',
  cookie: 'Cookie Policy',
  'data-deletion': 'Data Deletion Policy',
  aup: 'Acceptable Use Policy',
  dmca: 'DMCA / Copyright Policy',
};
const SLUGS = Object.keys(SLUG_META) as LegalSlug[];
const DEFAULT_TITLES = Object.values(SLUG_META);

const today = (): string => new Date().toISOString().slice(0, 10);

const makeEmptyForm = (): FormState => ({
  slug: 'terms', title: SLUG_META.terms, version: today(), body: '', effective_date: '',
});

const STATUS_CHIP: Record<Status, { label: string; bg: string; fg: string }> = {
  draft: { label: 'Draft', bg: '#FFF3E0', fg: '#B26A00' },
  published: { label: 'Published', bg: '#E8F5F3', fg: '#2D6E6A' },
  archived: { label: 'Archived', bg: '#ECEFF1', fg: '#546E7A' },
};

const tealContained = { bgcolor: '#2D6E6A', textTransform: 'none' as const, borderRadius: 2, '&:hover': { bgcolor: '#1F4E4A' } };
const tealOutlined = { textTransform: 'none' as const, borderRadius: 2, color: '#2D6E6A', borderColor: '#2D6E6A' };
const redOutlined = { textTransform: 'none' as const, borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f' };

const previewSx = {
  border: '1px solid #E6E0D6', borderRadius: 2, p: 2, bgcolor: '#FAFAF8', minHeight: 120,
  color: '#4A5E5C', fontSize: '0.95rem',
  '& h1, & h2, & h3': { color: '#1A2826', fontWeight: 700, mt: 2, mb: 1 },
  '& h1': { fontSize: '1.5rem' }, '& h2': { fontSize: '1.3rem' }, '& h3': { fontSize: '1.1rem' },
  '& p': { lineHeight: 1.7, mb: 1.5 },
  '& a': { color: '#2D6E6A' },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
  '& th, & td': { border: '1px solid #E6E0D6', px: 1, py: 0.5, textAlign: 'left' },
  '& th': { bgcolor: '#F0EDE6', fontWeight: 700 },
  '& ul, & ol': { pl: 3, mb: 1.5 },
  '& blockquote': { borderLeft: '3px solid #2D6E6A', pl: 1.5, ml: 0, fontStyle: 'italic' },
  '& code': { bgcolor: '#F0EDE6', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' },
  '& pre': { bgcolor: '#1A2826', color: '#fff', p: 1.5, borderRadius: 1, overflow: 'auto' },
};

type ConfirmState = { id: string; action: 'archive' | 'delete'; title: string; slug: string; version: string; status: Status };

const LegalDocumentsTab: React.FC = () => {
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(makeEmptyForm());
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/legal');
      const data = await res.json();
      if (data.success) setDocs(data.documents || []);
      else setError(data.error || 'Failed to load legal documents');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const previewHtml = useMemo(
    () => DOMPurify.sanitize(marked.parse(form.body || '', { breaks: true }) as string),
    [form.body]
  );

  const openNew = () => { setForm(makeEmptyForm()); setEditId('new'); };

  const openEdit = (d: LegalDoc) => {
    setForm({
      slug: d.slug,
      title: d.title,
      version: d.version,
      body: d.body || '',
      effective_date: d.effectiveDate ? String(d.effectiveDate).slice(0, 10) : '',
    });
    setEditId(d.id);
  };

  // Switching slug on a NEW doc updates an untouched default title; a custom title is kept.
  const onSlugChange = (slug: LegalSlug) => {
    setForm((f) => ({
      ...f,
      slug,
      title: editId === 'new' && (!f.title.trim() || DEFAULT_TITLES.includes(f.title)) ? SLUG_META[slug] : f.title,
    }));
  };

  const saveForm = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.version.trim()) { setError('Version is required (e.g. 2026-06-05)'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload: any = {
        slug: form.slug,
        title: form.title.trim(),
        version: form.version.trim(),
        body: form.body,
        body_format: 'markdown',
        effective_date: form.effective_date || null,
      };
      let res: Response;
      if (editId === 'new') {
        res = await api.post('/admin/legal', payload);
      } else {
        res = await apiRequest(`/admin/legal/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      }
      const data = await res.json();
      if (data.success) {
        setSuccess(editId === 'new' ? 'Document created as draft.' : 'Document updated.');
        setEditId(null);
        fetchDocs();
      } else {
        setError(data.error || 'Save failed');
      }
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: string, status: Status) => {
    setProcessing(id); setError(''); setSuccess('');
    try {
      const res = await api.post(`/admin/legal/${id}/status`, { status });
      const data = await res.json();
      if (data.success) {
        setSuccess(
          status === 'published'
            ? 'Published. Any previously published version of this document was archived.'
            : `Status set to ${status}.`
        );
        fetchDocs();
      } else {
        setError(data.error || 'Status change failed');
      }
    } catch {
      setError('Status change failed');
    } finally {
      setProcessing(null);
    }
  };

  const doDelete = async (id: string) => {
    setProcessing(id); setError(''); setSuccess('');
    try {
      const res = await api.delete(`/admin/legal/${id}`);
      const data = await res.json();
      if (data.success) { setSuccess('Document deleted.'); fetchDocs(); }
      else setError(data.error || 'Delete failed');
    } catch {
      setError('Delete failed');
    } finally {
      setProcessing(null);
    }
  };

  const runConfirm = () => {
    if (!confirm) return;
    const c = confirm;
    setConfirm(null);
    if (c.action === 'delete') doDelete(c.id);
    else setStatus(c.id, 'archived');
  };

  const renderActions = (d: LegalDoc) => {
    const busy = processing !== null || saving;
    const btns: React.ReactNode[] = [
      <Button key="edit" size="small" variant="outlined" disabled={busy} onClick={() => openEdit(d)} sx={tealOutlined}>Edit</Button>,
    ];
    if (d.status !== 'published') {
      btns.push(<Button key="pub" size="small" variant="contained" disabled={busy} onClick={() => setStatus(d.id, 'published')} sx={tealContained}>Publish</Button>);
    } else {
      btns.push(<Button key="hold" size="small" variant="outlined" disabled={busy} onClick={() => setStatus(d.id, 'draft')} sx={tealOutlined}>Hold</Button>);
    }
    if (d.status === 'archived') {
      btns.push(<Button key="restore" size="small" variant="outlined" disabled={busy} onClick={() => setStatus(d.id, 'draft')} sx={tealOutlined}>Restore</Button>);
    } else {
      btns.push(<Button key="archive" size="small" variant="outlined" disabled={busy} onClick={() => setConfirm({ id: d.id, action: 'archive', title: d.title, slug: d.slug, version: d.version, status: d.status })} sx={redOutlined}>Archive</Button>);
    }
    btns.push(<Button key="del" size="small" variant="outlined" disabled={busy} onClick={() => setConfirm({ id: d.id, action: 'delete', title: d.title, slug: d.slug, version: d.version, status: d.status })} sx={redOutlined}>Delete</Button>);
    return <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{btns}</Box>;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Legal Documents</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={tealContained}>New Document</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These power the public /terms, /privacy and /aup pages and stamp the version recorded on each signup consent.
        Only one version per document can be Published at a time — publishing a new version archives the previous one.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#2D6E6A' }} /></Box>
      ) : docs.length === 0 ? (
        <Alert severity="info">No legal documents yet. Use “New Document” to add one.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Document</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((d: LegalDoc) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{d.title}</Typography>
                    <Typography variant="caption" color="text.secondary">/{d.slug}</Typography>
                  </TableCell>
                  <TableCell>{d.version}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_CHIP[d.status].label}
                      sx={{ bgcolor: STATUS_CHIP[d.status].bg, color: STATUS_CHIP[d.status].fg, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{renderActions(d)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / edit form */}
      <Dialog open={editId !== null} onClose={() => setEditId(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editId === 'new' ? 'New Legal Document' : 'Edit Legal Document'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField select label="Document" size="small" sx={{ minWidth: 220 }}
                value={form.slug} disabled={editId !== 'new'}
                helperText={editId !== 'new' ? 'Locked after creation' : 'Which legal document this is'}
                onChange={(e) => onSlugChange(e.target.value as LegalSlug)}>
                {SLUGS.map((s) => (
                  <MenuItem key={s} value={s}>{SLUG_META[s]} (/{s})</MenuItem>
                ))}
              </TextField>
              <TextField label="Version" size="small" sx={{ minWidth: 160 }} placeholder="2026-06-05"
                value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                helperText="Stamped onto consent" />
              <TextField label="Effective date" size="small" type="date" sx={{ minWidth: 170 }}
                InputLabelProps={{ shrink: true }}
                value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                helperText="Optional (counsel decides)" />
            </Box>

            <TextField label="Title" size="small" fullWidth value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Body (Markdown — headings, lists, tables supported)</Typography>
            </Box>
            <TextField fullWidth multiline minRows={14}
              value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              helperText="# heading · **bold** · - list · > quote · [text](url) · | table | col |"
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }} />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Preview (matches the published page)
              </Typography>
              {form.body.trim() ? (
                <Box sx={previewSx} dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <Box sx={{ ...previewSx, color: '#9AA8A5', fontStyle: 'italic' }}>Start typing to see a preview…</Box>
              )}
            </Box>

            {editId === 'new' && (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                New documents are saved as <strong>Draft</strong>. Use <strong>Publish</strong> from the list to make a version live.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditId(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={saveForm}
            startIcon={saving ? <CircularProgress size={14} /> : undefined} sx={tealContained}>
            {editId === 'new' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm destructive actions */}
      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        {confirm && (() => {
          const wasPublished = confirm.status === 'published';
          const title = confirm.action === 'archive' ? 'Archive document?' : 'Delete this version permanently?';
          const body = confirm.action === 'archive'
            ? `Set “${confirm.title}” (/${confirm.slug}, ${confirm.version}) to Archived.${wasPublished ? ' This is the currently published version — the public page will stop showing it until you publish a version again.' : ''}`
            : `This permanently deletes “${confirm.title}” (/${confirm.slug}, ${confirm.version}) from Percoro. This cannot be undone.${wasPublished ? ' This is the currently published version — the public page will return “not found” until another version is published.' : ''}`;
          const cta = confirm.action === 'archive' ? 'Archive' : 'Delete permanently';
          return (
            <>
              <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
              <DialogContent><DialogContentText>{body}</DialogContentText></DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Back</Button>
                <Button variant="contained" onClick={runConfirm}
                  sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}>
                  {cta}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default LegalDocumentsTab;
